/* ============================================================================
   Performance operativa.

   Combina las cartas del RFM con el peso real y la atmósfera del punto para
   responder las preguntas que importan antes de despegar:
     ¿puedo mantener estacionario acá? ¿con cuánto margen? ¿me alcanza el
     combustible? ¿libro el terreno de la ruta?

   Todo resultado incluye la procedencia del dato que lo respalda. Un margen
   calculado sobre una carta no verificada no vale lo mismo que uno calculado
   sobre una verificada, y quien lo lee tiene que poder distinguirlos.
   ========================================================================== */

import { consultarCarta } from "./interpolacion.js";
import { resumenAtmosferico } from "./atmosfera.js";

/**
 * Evalúa el estacionario (OGE e IGE) en un punto.
 *
 * @param {object} perfil perfil de aeronave
 * @param {object} punto  { elevacionFt, oatC, qnhHpa }
 * @param {number} pesoActualKg peso real al llegar a ese punto
 */
export function evaluarEstacionario(perfil, punto, pesoActualKg) {
  const atmosfera = resumenAtmosferico({
    elevacionFt: punto?.elevacionFt,
    oatC: punto?.oatC,
    qnhHpa: punto?.qnhHpa,
  });

  // La entrada a las cartas del RFM es altitud PRESIÓN, no elevación.
  const entradaFt = atmosfera.altitudPresionFt;

  const oge = consultarCarta(perfil?.ogeTable, entradaFt, punto?.oatC);
  const ige = consultarCarta(perfil?.igeTable, entradaFt, punto?.oatC);

  const margen = (resultado) => {
    if (resultado.fueraDeRango || resultado.valor === null) return null;
    if (!Number.isFinite(pesoActualKg)) return null;
    return resultado.valor - pesoActualKg;
  };

  const margenOge = margen(oge);
  const margenIge = margen(ige);

  let veredicto;
  if (margenOge === null && margenIge === null) {
    veredicto = "SIN_DATO";
  } else if (margenOge !== null && margenOge >= 0) {
    veredicto = "OGE_DISPONIBLE";
  } else if (margenIge !== null && margenIge >= 0) {
    veredicto = "SOLO_IGE";
  } else {
    veredicto = "EXCEDIDO";
  }

  return {
    atmosfera,
    pesoActualKg,
    oge: { ...oge, margenKg: margenOge },
    ige: { ...ige, margenKg: margenIge },
    veredicto,
    explicacion: {
      SIN_DATO:
        "La carta no cubre esta combinación de altitud y temperatura. " +
        "Consultar el RFM directamente.",
      OGE_DISPONIBLE: "Estacionario fuera de efecto suelo disponible al peso actual.",
      SOLO_IGE:
        "Solo alcanza para estacionario DENTRO de efecto suelo. " +
        "Sin margen OGE: una salida vertical desde superficie confinada no es viable.",
      EXCEDIDO: "El peso supera el máximo admisible en estacionario para estas condiciones.",
    }[veredicto],
    // La UI está obligada a mostrar esto junto al número.
    procedencia: oge.procedencia ?? ige.procedencia ?? null,
  };
}

/**
 * Consumo horario en crucero, según la carta del perfil.
 */
export function consumoHorario(perfil, { altitudPresionFt, oatC }) {
  return consultarCarta(perfil?.fuelTable, altitudPresionFt, oatC);
}

/**
 * Planificación de combustible.
 *
 * Las reservas NO se fijan acá: dependen de la jurisdicción y de la regla de
 * vuelo, y ese módulo llega en la Fase 5. Hasta entonces se reciben como
 * parámetro explícito y la UI debe declarar de dónde salen. Poner un valor
 * por defecto disfrazado de norma sería el peor error posible en este módulo.
 *
 * @param {object} args
 * @param {number} args.tiempoVueloH tiempo estimado de la ruta
 * @param {number} args.consumoKgH consumo horario aplicable
 * @param {number} args.reservaMin reserva reglamentaria, en minutos
 * @param {number} args.contingenciaPct porcentaje de contingencia sobre el trayecto
 * @param {number} args.combustibleABordoKg
 */
export function planificarCombustible({
  tiempoVueloH,
  consumoKgH,
  reservaMin = 0,
  contingenciaPct = 0,
  combustibleABordoKg = null,
}) {
  if (!Number.isFinite(tiempoVueloH) || !Number.isFinite(consumoKgH)) {
    return {
      valido: false,
      motivo: "faltan el tiempo de vuelo o el consumo horario",
    };
  }

  const trayectoKg = tiempoVueloH * consumoKgH;
  const contingenciaKg = trayectoKg * (contingenciaPct / 100);
  const reservaKg = (reservaMin / 60) * consumoKgH;
  const requeridoKg = trayectoKg + contingenciaKg + reservaKg;

  const tieneABordo = Number.isFinite(combustibleABordoKg);
  const sobranteKg = tieneABordo ? combustibleABordoKg - requeridoKg : null;

  return {
    valido: true,
    trayectoKg,
    contingenciaKg,
    reservaKg,
    requeridoKg,
    combustibleABordoKg: tieneABordo ? combustibleABordoKg : null,
    sobranteKg,
    suficiente: tieneABordo ? sobranteKg >= 0 : null,
    // Autonomía total con lo que hay a bordo, incluida la reserva.
    autonomiaH: tieneABordo && consumoKgH > 0 ? combustibleABordoKg / consumoKgH : null,
    advertencia:
      reservaMin === 0
        ? "Reserva en cero: cargar el mínimo reglamentario de la jurisdicción aplicable."
        : null,
  };
}

/**
 * Franqueamiento de terreno a lo largo del perfil de ruta.
 *
 * @param {Array<{distanciaKm:number, elevacionFt:number|null}>} perfilTerreno
 * @param {number} altitudCruceroFt
 * @param {number} margenMinimoFt separación mínima deseada sobre el terreno
 * @param {number|null} techoServicioFt techo del tipo, si está definido
 */
export function analizarFranqueamiento(
  perfilTerreno,
  altitudCruceroFt,
  margenMinimoFt = 500,
  techoServicioFt = null,
) {
  const puntos = (perfilTerreno || []).filter((p) => Number.isFinite(p.elevacionFt));
  const sinDato = (perfilTerreno || []).length - puntos.length;

  if (puntos.length === 0) {
    return {
      valido: false,
      motivo: "sin datos de elevación en la ruta",
      puntosSinDato: sinDato,
    };
  }

  const masAlto = puntos.reduce((max, p) => (p.elevacionFt > max.elevacionFt ? p : max), puntos[0]);
  const margenMinimoRealFt = altitudCruceroFt - masAlto.elevacionFt;

  const conflictos = puntos
    .filter((p) => altitudCruceroFt - p.elevacionFt < margenMinimoFt)
    .map((p) => ({
      distanciaKm: p.distanciaKm,
      elevacionFt: p.elevacionFt,
      margenFt: altitudCruceroFt - p.elevacionFt,
    }));

  const superaTecho = Number.isFinite(techoServicioFt) && altitudCruceroFt > techoServicioFt;

  return {
    valido: true,
    puntoMasAlto: masAlto,
    margenMinimoRealFt,
    margenMinimoRequeridoFt: margenMinimoFt,
    libra: margenMinimoRealFt >= margenMinimoFt,
    conflictos,
    superaTecho,
    // Un perfil con huecos no es un perfil: el pico real puede estar en el hueco.
    puntosSinDato: sinDato,
    perfilCompleto: sinDato === 0,
    advertencia:
      sinDato > 0
        ? `${sinDato} punto(s) sin elevación: el terreno más alto podría no estar representado.`
        : null,
  };
}

/**
 * Peso máximo real en un punto: el MENOR entre lo que dan las cartas y el
 * peso máximo estructural de la aeronave.
 *
 * Las cartas de estacionario del AS350 llegan hasta 2800 kg porque contemplan
 * carga externa. Mostrar "máx. OGE 2785 kg" en un punto al nivel del mar es
 * cierto para la carta y falso para el vuelo: con carga interna el límite es
 * 2250 kg y manda ese. Enseñar el número de la carta a secas invita a cargar
 * media tonelada de más.
 *
 * Se informa cuál de los tres manda, porque no es lo mismo estar limitado por
 * la estructura que por la potencia disponible ese día.
 *
 * @param {{ogeKg:number|null, igeKg:number|null, maximoEstructuralKg:number|null}} datos
 */
export function limiteDePeso({ ogeKg = null, igeKg = null, maximoEstructuralKg = null } = {}) {
  const candidatos = [
    { kg: ogeKg, cual: "OGE" },
    { kg: igeKg, cual: "IGE" },
    { kg: maximoEstructuralKg, cual: "máximo estructural" },
  ].filter((c) => Number.isFinite(c.kg));

  if (candidatos.length === 0) {
    return { limiteKg: null, limitante: null, ogeKg: null, igeKg: null, sinDato: true };
  }

  // El estacionario fuera del efecto suelo es el caso exigente: si se puede
  // sostener OGE, se puede sostener IGE. Cuando falta el OGE manda el IGE, y la
  // app tiene que decir que el margen se está midiendo con la carta menos
  // exigente.
  const referenciaEstacionario = Number.isFinite(ogeKg) ? { kg: ogeKg, cual: "OGE" }
    : Number.isFinite(igeKg) ? { kg: igeKg, cual: "IGE" }
      : null;

  const aplicables = [referenciaEstacionario, Number.isFinite(maximoEstructuralKg) ? { kg: maximoEstructuralKg, cual: "máximo estructural" } : null]
    .filter(Boolean);

  const manda = aplicables.reduce((a, b) => (b.kg < a.kg ? b : a));

  return {
    limiteKg: manda.kg,
    limitante: manda.cual,
    ogeKg: Number.isFinite(ogeKg) ? ogeKg : null,
    igeKg: Number.isFinite(igeKg) ? igeKg : null,
    // Solo con IGE no se puede afirmar que el punto sea operable fuera del
    // efecto suelo, que es lo que hace falta para una aproximación a una zona
    // sin superficie preparada.
    soloIGE: !Number.isFinite(ogeKg) && Number.isFinite(igeKg),
    sinDato: false,
  };
}

/**
 * Corrección por consumo eléctrico alto.
 *
 * El encabezado de la carta IGE del RFM (Figura 5) dice:
 *
 *   ELECTRICAL CONSUMPTION > 50 A: PERFORMANCE IS REDUCED BY 50 kg (110 lb)
 *
 * No es letra chica. Con el faro de búsqueda, equipo calefaccionado o el
 * cabrestante en uso se pasa de 50 A sin dificultad, y el peso máximo real
 * queda 50 kg por debajo del que muestra la carta. La aplicación no lo
 * modelaba: mostraba el número de la carta como si fuera el límite, y 50 kg
 * son media persona.
 *
 * Se aplica a lo que la carta devuelve, no al peso bruto: es el LÍMITE el que
 * baja, no el helicóptero el que pesa más.
 *
 * @param {number|null} pesoMaximoKg lo que da la carta
 * @param {boolean} consumoAlto si el consumo eléctrico supera el umbral
 */
export function corregirPorConsumoElectrico(pesoMaximoKg, consumoAlto, restaKg = 50) {
  if (!Number.isFinite(pesoMaximoKg)) {
    return { pesoKg: null, aplicada: false, restaKg: 0 };
  }
  if (!consumoAlto) return { pesoKg: pesoMaximoKg, aplicada: false, restaKg: 0 };
  return {
    pesoKg: pesoMaximoKg - restaKg,
    aplicada: true,
    restaKg,
    motivo: `Consumo eléctrico sobre 50 A: la carta del RFM resta ${restaKg} kg al peso máximo.`,
  };
}
